/**
 * Refactored Pinned clients list component using modular components
 * Reduced from 600+ lines to ~200 lines by extracting specialized components
 */

import React, { useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import ClientCards from './ClientCards';
import ClientTable from './ClientTable';
import EmailContactDialog from '../dialogs/EmailContactDialog';
import ExportForm from '../dialogs/ExportForm';

// Import split components
import PinnedListHeader from './pinned-list/PinnedListHeader';
import PinnedListActions from './pinned-list/PinnedListActions';
import PinnedListEmptyState from './pinned-list/PinnedListEmptyState';
import PinnedListTierFilters from './pinned-list/PinnedListTierFilters';
import PinnedListNotifications from './pinned-list/PinnedListNotifications';
import RetentionToggles from './pinned-list/RetentionToggles';
import { calculateRetentionPercentages, calculateNewClientsPercent, getYearAndPrevious } from '../../utils/yearSpecificUtils';
import { isExternalClient as utilIsExternalClient, isFromDifferentManager as utilIsFromDifferentManager } from '../../utils/clientUtils';
import { getRevenueColumnHeader } from '../../services/conferenceRevenueService';

// Import specialized hooks
import usePinnedListState from '../../hooks/usePinnedListState';
import usePinnedListFilters from '../../hooks/usePinnedListFilters';

// Import export utilities
import ExportUtils from './export/ExportUtils';
import { usePinnedListExcelExport } from './export/PinnedListExcelExporter';

// Import CSS styles
import '../../styles/components/clients/PinnedClientsList.css';

/**
 * Component for displaying and managing pinned clients using Context and split components
 * @returns {JSX.Element} PinnedClientsList component
 */
const PinnedClientsList = () => {
  // State for dialogs and UI
  const [showExportForm, setShowExportForm] = useState(false);
  const [exportType, setExportType] = useState(''); // 'email' or 'excel'
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailContactInfo, setEmailContactInfo] = useState({
    name: '',
    email: '',
    clientName: ''
  });

  // Retention filter state
  // DISABLED: Retention filter temporarily hidden from UI until further notice
  // The filter logic and calculations remain in code for future reintroduction
  const [retentionFilter, setRetentionFilter] = useState('default');

  // Auth context
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Get all needed data and methods from context
  const {
    // Pinned clients
    pinnedClients,
    orderedPinnedClients,
    pinnedViewType,
    portfolioType,
    activeManager,
    isHardPinned,
    useUniformColor,
    useTierColors,
    usePieChartMarkers,

    // Filters (including selectedYear)
    filters: { selectedYear },
    
    // Conference selection
    selectedConference,
    
    // Sponsor data
    sponsorData,
    
    // Conference sales data
    conferenceSalesData,

    // Methods
    unpinClient,
    unpinAllClients,
    toggleViewType,
    toggleHardPin,
    toggleUniformColor,
    setUseTierColors,
    setUsePieChartMarkers,

    // State setters for direct manipulation
    setPinnedClients,
    setOrderedPinnedClients,

    // Client state methods
    isClientLocked,
    toggleClientLock,
    isCardFlipped,
    toggleCardFlip,
    isTableRowExpanded,
    toggleTableRowExpansion
  } = useAppContext();

  // Get pinned list state from context (now shared with map)
  const {
    sortConfig,
    pinnedSearchQuery,
    activeTierFilters,
    setPinnedSearchQuery,
    setActiveTierFilters,
    requestSort,
    getSortDirectionIndicator
  } = useAppContext();

  // DEBUG: Log retention filter and client count
  if (typeof window !== "undefined" && window.__DEBUG_RETENTION) {
    // eslint-disable-next-line no-console
    console.log("PinnedClientsList: retentionFilter", retentionFilter, "selectedYear", selectedYear, "orderedPinnedClients", orderedPinnedClients.length);
  }
  // For "lost" filter, use the union of clients from selected year and previous year
  const { clientData } = useAppContext();
  const getUnionClientsForRetention = () => {
    if (selectedYear !== "All" && activeManager) {
      // Find available years
      const yearsSet = new Set();
      clientData.forEach(client => {
        Object.keys(client).forEach(key => {
          const match = key.match(/^(\d{4}) Revenue$/);
          if (match) yearsSet.add(match[1]);
        });
      });
      const availableYears = Array.from(yearsSet).sort();
      const idx = availableYears.indexOf(selectedYear);
      if (idx > 0) {
        const prevYear = availableYears[idx - 1];
        // Only include clients for the selected manager
        const managerClients = clientData.filter(
          c => c.conference_manager === activeManager
        );
        // Get all manager clients who booked in either selectedYear or prevYear
        const bookedPrev = managerClients.filter(c => c["client_name"] && (
          c[`${prevYear} Rooms`] > 0 ||
          c[`${prevYear} RN`] > 0 ||
          (typeof c[`${prevYear} Revenue`] === "string" && parseFloat(c[`${prevYear} Revenue`].replace(/[$,]/g, "")) > 0)
        ));
        const bookedCurrent = managerClients.filter(c => c["client_name"] && (
          c[`${selectedYear} Rooms`] > 0 ||
          c[`${selectedYear} RN`] > 0 ||
          (typeof c[`${selectedYear} Revenue`] === "string" && parseFloat(c[`${selectedYear} Revenue`].replace(/[$,]/g, "")) > 0)
        ));
        // Union by client_name + country
        const key = c => `${c.client_name}__${c.country}`;
        const unionMap = new Map();
        bookedPrev.forEach(c => unionMap.set(key(c), c));
        bookedCurrent.forEach(c => unionMap.set(key(c), c));
        return Array.from(unionMap.values());
      }
    }
    // Default: use orderedPinnedClients
    return orderedPinnedClients;
  };

  // Always use the union set for all retention filters (not just "lost")
  const {
    sortedClients,
    calculateFilteredRevenue
  } = usePinnedListFilters(
    getUnionClientsForRetention(),
    pinnedSearchQuery,
    activeTierFilters,
    sortConfig,
    new Set(), // We'll use context's locking instead
    { retentionFilter }
  );

  // Calculate total revenue using export utils
  const calculateTotalRevenue = () => ExportUtils.calculateTotalRevenue(pinnedClients);
  
  // Use Excel export hook
  const { exportToExcel: exportToExcelHook } = usePinnedListExcelExport();
  
  // Handle tier filter toggle
  const toggleTierFilter = (tier) => {
    const newFilters = new Set(activeTierFilters);
    if (newFilters.has(tier)) {
      newFilters.delete(tier);
    } else {
      newFilters.add(tier);
    }
    setActiveTierFilters(newFilters);
  };
  
  // Check if a client should have textured styling (only manually locked clients)
  const isExternalClient = (client) => {
    return utilIsExternalClient(client, isClientLocked);
  };

  // Check if a client is from a different manager (for badge display only)
  const isFromDifferentManager = (client) => {
    return utilIsFromDifferentManager(client, portfolioType, activeManager);
  };

  // Excel export function using the extracted hook
  const exportToExcel = async () => {
    setShowExportForm(false); // Ensure modal is closed first
    setTimeout(async () => {
      await exportToExcelHook(
        sortedClients, 
        portfolioType, 
        activeManager, 
        calculateTotalRevenue, 
        showTemporaryNotification
      );
    }, 300); // Delay to allow modal to close before showing notification
  };

  // Handle export form submission
  const handleExportFormSubmit = (result) => {
    setShowExportForm(false);
    if (result && result.success) {
      showTemporaryNotification(result.message);
    } else if (result && result.message) {
      showTemporaryNotification(`Error: ${result.message}`);
    } else {
      showTemporaryNotification('Export completed');
    }
  };

  // Show a temporary notification
  const showTemporaryNotification = (message) => {
    setNotificationMessage(message);
    setShowNotification(true);
    
    setTimeout(() => {
      setShowNotification(false);
    }, 7000); // Increased duration to 7 seconds
  };

  // Open export form
  const openExportForm = (type) => {
    setExportType(type);
    setShowExportForm(true);
  };

  // Define table columns for table view
  const tableColumns = [
    {
      key: 'client_name',
      label: 'Client Name',
      sortable: true,
      onSort: requestSort,
      sortIndicator: getSortDirectionIndicator('client_name'),
      defaultWidth: 200
    },
    {
      key: 'country',
      label: 'Country',
      sortable: true,
      onSort: requestSort,
      sortIndicator: getSortDirectionIndicator('country'),
      defaultWidth: 120
    },
    {
      key: 'group_size',
      label: 'Total Rooms',
      sortable: true,
      onSort: requestSort,
      sortIndicator: getSortDirectionIndicator('group_size'),
      defaultWidth: 100
    },
    {
      key: 'conference_manager',
      label: 'Manager',
      sortable: true,
      onSort: requestSort,
      sortIndicator: getSortDirectionIndicator('conference_manager'),
      defaultWidth: 150
    },
    {
      key: 'tier',
      label: 'Tier',
      sortable: true,
      onSort: requestSort,
      sortIndicator: getSortDirectionIndicator('tier'),
      defaultWidth: 80
    },
    {
      key: 'revenue',
      label: getRevenueColumnHeader(selectedConference, selectedYear),
      sortable: true,
      onSort: requestSort,
      sortIndicator: getSortDirectionIndicator('revenue'),
      defaultWidth: 150
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      defaultWidth: 120
    }
  ];

  // Helper: get current/last year for slider labels
  const { year: currentYear, prevYear: lastYear } = getYearAndPrevious(selectedYear);

  // Calculate retention percentages for slider labels
  const { retainedPercent, lostPercent } = calculateRetentionPercentages(
    clientData,
    selectedYear,
    activeManager
  );
  // Calculate new clients percent for slider label
  const newClientsPercent = calculateNewClientsPercent(
    clientData,
    selectedYear,
    activeManager
  );

  // If no clients, show empty state using extracted component
  return (
    <div className="pinned-clients-list">
      <div className="pinned-clients-list__header">
        <PinnedListHeader 
          portfolioType={portfolioType}
          activeManager={activeManager}
          isHardPinned={isHardPinned}
          pinnedClients={pinnedClients}
          calculateTotalRevenue={calculateTotalRevenue}
        />

        {/* DISABLED: Retention slider temporarily hidden from UI until further notice */}
        {/* The slider component and filter logic remain in code for future reintroduction */}
        {/* Spacer to maintain header height while slider is hidden */}
        <div
          style={{
            margin: "0.5rem 0",
            minHeight: 48
          }}
        />
        {/* 
        <div
          style={{
            margin: "0.5rem 0",
            minHeight: 48, // Reserve space for the slider (adjust as needed)
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <div
            style={{
              width: "100%",
              opacity: selectedYear !== 'All' && pinnedClients.length > 0 ? 1 : 0,
              pointerEvents: selectedYear !== 'All' && pinnedClients.length > 0 ? "auto" : "none",
              transition: "opacity 0.2s"
            }}
          >
            <RetentionToggles
              value={retentionFilter}
              onChange={setRetentionFilter}
              disabled={!isAdmin}
              currentYear={currentYear}
              lastYear={lastYear}
              retainedPercent={retainedPercent}
              lostPercent={lostPercent}
              newClientsPercent={newClientsPercent}
            />
          </div>
        </div>
        */}

        <div className="pinned-clients-list__actions">
          {/* Use extracted tier filters component */}
          <PinnedListTierFilters 
            pinnedClients={pinnedClients}
            activeTierFilters={activeTierFilters}
            onToggleTierFilter={toggleTierFilter}
          />

          <PinnedListActions
            pinnedClients={pinnedClients}
            pinnedSearchQuery={pinnedSearchQuery}
            setPinnedSearchQuery={setPinnedSearchQuery}
            sortedClients={sortedClients}
            calculateFilteredRevenue={calculateFilteredRevenue}
            activeTierFilters={activeTierFilters}
            setActiveTierFilters={setActiveTierFilters}
            viewType={pinnedViewType}
            toggleViewType={toggleViewType}
            useUniformColor={useUniformColor}
            setUseUniformColor={toggleUniformColor}
            useTierColors={useTierColors}
            setUseTierColors={setUseTierColors}
            usePieChartMarkers={usePieChartMarkers}
            setUsePieChartMarkers={setUsePieChartMarkers}
            isHardPinned={isHardPinned}
            toggleHardPin={toggleHardPin}
            openExportForm={openExportForm}
            exportToExcel={exportToExcel}
            unpinAllClients={unpinAllClients}
            isAdmin={isAdmin}
            isClientLocked={isClientLocked}
            unpinClient={unpinClient}
            setPinnedClients={setPinnedClients}
            setOrderedPinnedClients={setOrderedPinnedClients}
          />
        </div>
      </div>
      
      <div className="pinned-clients-list__content">
        {pinnedClients.length === 0 ? (
          <PinnedListEmptyState 
            pinnedClients={pinnedClients}
            activeTierFilters={activeTierFilters}
            pinnedSearchQuery={pinnedSearchQuery}
            onClearFilters={() => {
              setActiveTierFilters(new Set());
              setPinnedSearchQuery('');
            }}
          />
        ) : (
          sortedClients.length === 0 ? (
            <>
              <PinnedListEmptyState 
                pinnedClients={pinnedClients}
                activeTierFilters={activeTierFilters}
                pinnedSearchQuery={pinnedSearchQuery}
                onClearFilters={() => {
                  setActiveTierFilters(new Set());
                  setPinnedSearchQuery('');
                }}
              />
              {/* DISABLED: Retention filter message hidden along with slider */}
              {/* 
              {retentionFilter !== 'default' && selectedYear !== 'All' && (
                <div style={{ textAlign: "center", color: "#888", marginTop: "1.5rem" }}>
                  No clients match the <b>{retentionFilter.charAt(0).toUpperCase() + retentionFilter.slice(1)}</b> filter for {selectedYear}.
                  <br />
                  (A "Lost" client is one who booked in a previous year but not in {selectedYear}.)
                </div>
              )}
              */}
            </>
          ) : (
            <div className="client-list-container">
              {pinnedViewType === 'table' ? (
                <ClientTable 
                  clients={sortedClients}
                  columns={tableColumns}
                  unpinClient={unpinClient}
                  isClientLocked={isClientLocked}
                  toggleClientLock={toggleClientLock}
                  isTableRowExpanded={isTableRowExpanded}
                  toggleTableRowExpansion={toggleTableRowExpansion}
                  isExternalClient={isExternalClient}
                  useUniformColor={useUniformColor}
                  selectedYear={selectedYear}
                />
              ) : (
                <ClientCards
                  clients={sortedClients}
                  unpinClient={unpinClient}
                  isClientLocked={isClientLocked}
                  toggleClientLock={toggleClientLock}
                  isCardFlipped={isCardFlipped}
                  toggleCardFlip={toggleCardFlip}
                  isExternalClient={isExternalClient}
                  useUniformColor={useUniformColor}
                  handleDragStart={(e, client, index) => {
                    // Implement drag start logic
                  }}
                  handleDragOver={(e, index) => {
                    // Implement drag over logic
                  }}
                  handleDragEnd={() => {
                    // Implement drag end logic
                  }}
                  draggedItem={null} // Will need to add drag state to context
                  setEmailContactInfo={setEmailContactInfo}
                  setShowEmailDialog={setShowEmailDialog}
                  selectedYear={selectedYear}
                  sponsorData={sponsorData}
                  selectedConference={selectedConference}
                  conferenceSalesData={conferenceSalesData}
                />
              )}
            </div>
          )
        )}
      </div>
      
      {/* Export Form Modal */}
      {showExportForm && (
        <ExportForm 
          onClose={() => setShowExportForm(false)} 
          onSubmit={handleExportFormSubmit}
          exportType={exportType}
          clients={sortedClients}
        />
      )}
      
      {/* Use extracted notification component */}
      <PinnedListNotifications 
        showNotification={showNotification}
        notificationMessage={notificationMessage}
        onClose={() => setShowNotification(false)}
      />
      
      {/* Email Contact Dialog */}
      {showEmailDialog && (
        <EmailContactDialog
          contactName={emailContactInfo.name}
          contactEmail={emailContactInfo.email}
          clientName={emailContactInfo.clientName}
          onClose={(result) => {
            setShowEmailDialog(false);
            if (result.success) {
              showTemporaryNotification(result.message);
            }
          }}
        />
      )}
    </div>
  );
};

export default PinnedClientsList;
